import { useEffect, useState } from 'react';

import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { encodeQueryParams, StringParam } from 'use-query-params';

import { TRole } from '@/shared/types/role';

import { ACTOR_ROUTES, DIRECTOR_ROUTES, ROUTES } from '@/shared/constants';

interface UseRouteChangeWarningWithModalProps {
  isEnabled: boolean;
  onConfirmLeave: () => void;
  onCancelLeave: () => void;
}

/**
 * Хук, блокирующий переход по роуту (включая изменение query),
 * с возможностью подтвердить/отменить через модалку.
 * @param root0 - пропсы
 * @param root0.isEnabled - флаг, показывающий активен ли хук
 * @param root0.onConfirmLeave - функция, выполняемая при подтверждении перехода
 * @param root0.onCancelLeave - функция, выполняемая при отмене перехода
 */
export const useRouteChangeWarningWithModal = ({
  isEnabled,
  onConfirmLeave,
  onCancelLeave
}: UseRouteChangeWarningWithModalProps) => {
  const [nextUrl, setNextUrl] = useState<string | null>(null);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const currentFull = `${currentLocation.pathname}${currentLocation.search}`;
    const nextFull = `${nextLocation.pathname}${nextLocation.search}`;

    // Блокируем не только смену path, но и смену query(для табов)
    if (currentFull === nextFull) return false;

    if (isEnabled) {
      setNextUrl(nextFull);
      return true; // блокируем переход
    }
    return false;
  });

  /**
   * Подтверждение перехода
   */
  const confirmNavigation = () => {
    if (nextUrl) {
      blocker.proceed?.();
      onConfirmLeave();
    }
  };

  /**
   * Отмена перехода
   */
  const cancelNavigation = () => {
    setNextUrl(null);
    blocker.reset?.();
    onCancelLeave();
  };

  return {
    isBlocked: blocker.state === 'blocked',
    confirmNavigation,
    cancelNavigation
  };
};

/**
 * хук для предупреждения о закрытии/обновлении страницы
 * @param message - сообщение
 * @param isEnabled - флаг, показывающий активен ли хук, например, активировать только если есть несохраненные данные
 */
export const useBrowserTabCloseWarning = (
  message: string,
  isEnabled: boolean = true
) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isEnabled) return;

    /**
     * ф-ия выполяющаяся при событии выгрузки страницы
     * @param event - эвент браузера при выгрузке страницы
     */
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message; // в большинстве современных браузеров нельзя поменять стандартное сообщение,
      //  но в некоторых работает
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location, navigate, message, isEnabled]);
};

/**
 * хук для предупреждения о смене роута (переход по ссылке на сайте, нажатие назад/вперед)
 * @param message - сообщение предупреждающее о смене роута
 * @param isEnabled - флаг, указывающий активен ли хук
 */
export const useRouteChangeWarning = (
  message: string,
  isEnabled: boolean = true
) => {
  useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname === nextLocation.pathname) return false;
    return isEnabled ? !window.confirm(message) : false;
  });
};

/**
 * Строит путь навигации из глобального поиска по роли.
 * @param role Роль пользователя
 * @param searchValue Значение из поля поиска
 */
export const getSearchNavPath = (role: TRole, searchValue: string): string => {
  let path: string = ROUTES.root;
  let encodedQuery = {};

  if (role === 'director') {
    encodedQuery = encodeQueryParams(
      { name: StringParam },
      { name: JSON.stringify(searchValue) }
    );
    path = DIRECTOR_ROUTES.actors.index.route;
  } else {
    encodedQuery = encodeQueryParams(
      { name: StringParam, tab: StringParam },
      {
        name: JSON.stringify(searchValue),
        tab: ACTOR_ROUTES.events.index.tabs.castings.route
      }
    );
    path = ACTOR_ROUTES.events.index.route;
  }

  const searchParams = new URLSearchParams(
    encodedQuery as Record<string, string>
  );

  return `${path}?${searchParams}`;
};
